import React from 'react';

import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, ContainerOutlined, FileExcelOutlined, FileImageOutlined, FilePdfOutlined, FilePptOutlined, FileTextOutlined, FileUnknownOutlined, FileWordOutlined, FileZipOutlined, LinkOutlined } from '@ant-design/icons';
import 'dayjs/locale/en';
import 'dayjs/locale/tr';

import { getInitialLanguage } from '../../redux/languageSwitcher/config';
import useNumberFormat from '../../config/numberFormat';
import siteConfig from "@iso/config/site.config";
import localStorageUtil from '@iso/components/utility/localStorageUtil';
import { apiStatusManagement } from '@iso/lib/helpers/apiStatusManagement';
import DateDisplay from '../../components/utility/DateDisplay';
import { FormatPercentage } from '../../config/resultNumberFormat';
import { LOCAL_STORAGE_KEY, URL_PARAMS } from '../../assets/styles/constants';
import enumerations from '../../config/enumerations';

dayjs.locale('tr');

const { Link, Text } = Typography;

//TODO: Öncelikli olarak jwtToken değerini cookie'e ata ve session properties'i uygula
async function getSessionProperties(setSessionProperties) {
    let response;
    await fetch(`${siteConfig.api.security.getSessionProperties}`, buildRequestOptions('GET'))
        .then(response => {
            const status = apiStatusManagement(response, true);
            return status;
        })
        .then(res => {
            if (typeof setSessionProperties === 'function') {
                setSessionProperties(res);
            }
            return response = res;
        })
        .catch(error => console.log(error));
    return response;
}

function checkAccessPermission(roleName, sessionProperties) {
    let rNames = null;
    if (typeof roleName === 'string') {
        rNames = [roleName];
    } else if (Array.isArray(roleName)) {
        rNames = roleName;
    } else {
        return false;
    }
    if (sessionProperties && sessionProperties.roleName) {
        let role = sessionProperties.roleName;

        if (rNames.indexOf(role) >= 0) {
            return true;
        } else {
            return false;
        }
    } else {
        return false;
    }
}

function buildRequestOptions(method = "GET", body = null) {
    const language = getInitialLanguage();
    let currentVersion;
    try {
        const packageJson = require('./../../../package.json');
        currentVersion = packageJson.currentVersion;
    } catch (error) {
        console.log(error);
    }

    if (!currentVersion) {
        currentVersion = localStorageUtil.getItem(LOCAL_STORAGE_KEY.CURRENT_VERSION);
    }

    const regexPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;
    const isValid = regexPattern.test(currentVersion);

    const requestOptions = {
        method: method,
        headers: {
            "Content-Type": "application/json",
            "X-SCOOP-Version": isValid ? currentVersion : "N/A",
            "X-SCOOP-Referer": window.location.href || "N/A",
            "X-SCOOP-Lang": language
        },
        credentials: 'include'
    };
    if (body) {
        requestOptions['body'] = JSON.stringify(body);
    }
    return requestOptions;
}

function findMenuKeys(menuItems, path) {
    for (const item of menuItems) {
        if (item.key === path) {
            return { selectedKey: item.key, openKey: null };
        }
        if (item.children) {
            const found = findMenuKeys(item.children, path);
            if (found.selectedKey) {
                return { selectedKey: found.selectedKey, openKey: item.key };
            }
        }
    }
    return { selectedKey: null, openKey: null };
}

function handleDateRangeChange(dates, dateStrings, selectedRange, setFromDate, setToDate) {
    if (Array.isArray(dates)) {
        setFromDate(dates[0]);
        setToDate(dates[1]);
    } else if (selectedRange === 'start') {
        setFromDate(dates);
    } else if (selectedRange === 'end') {
        setToDate(dates);
    } else {
        setToDate(null);
        setFromDate(null);
    }
}

function titleCase(str) {
    str.toLowerCase();
    var strAr = str.split(" ");
    for (var i = 0; i < strAr.length; i++) {
        strAr[i] = strAr[i].charAt(0).toUpperCase() + strAr[i].substring(1).toLocaleLowerCase('tr-TR');
    }
    str = strAr.join(" ");

    return str;
}

function isStrInteger(value) {
    return /^\d+$/.test(value);
}

function parseJSON(data) {
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch (e) {
            return null;
        }
    } else if (typeof data === 'object' && data !== null) {
        return data;
    } else {
        return null;
    }
}

function formatCamelToHyphen(input) {
    return input
        .replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

function mimeToExtension(mime) {
    const mimeMap = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/bmp': 'bmp',
        'image/webp': 'webp'
        // Other mimetypes can be added here
    };
    return mimeMap[mime] || 'unknown';
}

function translateTabName(tabKey) {
    if(!tabKey) return '';
    const intl = useIntl();

    return intl.formatMessage({ id: tabKey })
}

function getFileTypeExtensionFromContentType(contentType) {
    switch (contentType) {
        case 'application/pdf':
            return 'pdf';
        case 'application/msword':
            return 'doc';
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return 'docx';
        case 'application/vnd.ms-excel':
            return 'xls';
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return 'xlsx';
        case 'application/vnd.ms-powerpoint':
            return 'ppt';
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return 'pptx';
        case 'text/plain':
            return 'txt';
        case 'image/jpeg':
            return 'jpg';
        case 'image/png':
            return 'png';
        case 'application/zip':
        case "application/x-compressed":
        case 'application/x-zip-compressed':
            return 'zip';
        default:
            return 'txt';
    }
}

const getFileIcon = (contentType) => {
    const commonComponentStyle = { fontSize: 24 };

    switch (contentType) {
        case "application/pdf":
            return <FilePdfOutlined style={commonComponentStyle} />;
        case "application/msword":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            return <FileWordOutlined style={commonComponentStyle} />;
        case "application/vnd.ms-excel":
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            return <FileExcelOutlined style={commonComponentStyle} />;
        case "application/vnd.ms-powerpoint":
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            return <FilePptOutlined style={commonComponentStyle} />;
        case "text/plain":
            return <FileTextOutlined style={commonComponentStyle} />;
        case "image/jpeg":
        case "image/png":
            return <FileImageOutlined style={commonComponentStyle} />;
        case "application/zip":
        case "application/x-compressed":
        case "application/x-zip-compressed":
            return <FileZipOutlined style={commonComponentStyle} />;
        default:
            return <FileUnknownOutlined style={commonComponentStyle} />;
    }
};

const formatSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
        return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
        return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else if (sizeInBytes < 1024 * 1024 * 1024) {
        return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    } else {
        return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
};

function renderTreeSelect(child, level = 0, highlightLimit) {
    const colorPalette = ['#000000', '#222222', '#444444', '#666666', '#888888'];

    return child.map((it) => {
        const opacity = Math.max(0, 1 - (level * 0.13));

        // Başlık parçalara ayrılır
        const titleParts = it.title.split(' ');
        let boldPart = '';
        let restOfTitle = it.title;

        if (typeof highlightLimit === 'number' && highlightLimit > 0) {
            boldPart = titleParts.slice(0, highlightLimit).join(' ');
            restOfTitle = titleParts.slice(highlightLimit).join(' ');
        }

        const titleElement = (
            <div style={{ opacity }}>
                {boldPart && <span style={{ color: colorPalette[level], fontWeight: 'bold' }}>{boldPart} </span>}
                <span style={{ color: colorPalette[level] }}> {restOfTitle}</span>
            </div>
        );

        // Eğer alt öğeler varsa, onları işleriz (recursively)
        if (it.children) {
            return {
                ...it,
                titleText: it.title,
                title: titleElement,
                children: renderTreeSelect(it.children, level + 1, highlightLimit)
            };
        } else {
            return {
                ...it,
                titleText: it.title,
                title: titleElement,
            };
        }
    });
}


function filterTreeNode(value, treeNode) {
    if (value && treeNode && treeNode.titleText) {
        const filterValue = value.toLocaleLowerCase('tr');
        const treeNodeTitle = treeNode.titleText.toLocaleLowerCase('tr');
        return treeNodeTitle.indexOf(filterValue) !== -1;
    }
    return false;
}

function getCalculationTypeColor(type) {
    const calculationTypeColorMap = {
        IPCC: 'processing',
        DEFRA: 'success',
        TR: 'error',
        PROVIDED_EMISSION: 'warning',
        SUPPLIER_ALLOCATION: 'magenta',
        default: ''
    };


    return calculationTypeColorMap[type] || calculationTypeColorMap.default;
}

/**
 * Returns a display label for emission categories.
 * @param {string} calculationType - The internal emission category name.
 * @returns {string} - The localized display label.
 */
function getCalculationTypeLabel(calculationType) {
    const intl = useIntl();

    switch (calculationType) {
        case 'SUPPLIER_ALLOCATION':
            return intl.formatMessage({ id: "emission.type.supplierAllocation" });
        case 'PROVIDED_EMISSION':
            return intl.formatMessage({ id: "emission.type.providedEmission" });
        default:
            return calculationType;
    }
}

const tableFunctions = {
    renderFunctions: {
        general: {
            index: (_, record, index) => (
                index + 1
            )
        },
        userList: {
            isLocked: (item) => (
                !item ?
                    <Tag color='green' key={item} bordered={false}><FormattedMessage id="app.open" /></Tag> :
                    <Tag color='red' key={item} bordered={false}><FormattedMessage id="app.closed" /></Tag>
            ),
            roleDescription: (_, record) => (
                record.role.roleDescription
            ),
            canCustomerViewFiles: (item) => (
                item ?
                    <CheckCircleTwoTone twoToneColor={"#00C853"} /> :
                    <CloseCircleTwoTone twoToneColor={"#FF1744"} />
            ),
            shortTitle: (item, record) => (
                record && record.client && record.client.shortTitle ? record.client.shortTitle : '-'
            )
        },
        clients: {
            fullTitle: (item) => (
                <Link href={`/app/dashboard/`} >{item}</Link>
            ),
            isActive: (item) => (
                item ?
                    <Tag color='green' key={item} bordered={false}><FormattedMessage id="app.open" /></Tag> :
                    <Tag color='red' key={item} bordered={false}><FormattedMessage id="app.closed" /></Tag>
            ),
            phone: (item) => {
                function formatPhoneNumber(phoneNumber) {
                    let cleaned = ('' + phoneNumber).replace(/\D/g, '');
                    if (cleaned[0] === '0') {
                        cleaned = cleaned.substring(1);
                    }

                    const match = cleaned.match(/^(\d{3})(\d{3})(\d{2})(\d{2})$/);
                    if (match) {
                        return '0 (' + match[1] + ') ' + match[2] + ' ' + match[3] + ' ' + match[4];
                    }
                    return phoneNumber;
                }

                return formatPhoneNumber(item);
            }
        },
        plants: {
            customerFullTitle: (item, record) => (
                record.client && <Link href={`/app/dashboard`} >{record.client.shortTitle}</Link>
            ),
            isActive: (item) => (
                item ?
                    <Tag color='green' key={item} bordered={false}><FormattedMessage id="app.open" /></Tag> :
                    <Tag color='red' key={item} bordered={false}><FormattedMessage id="app.closed" /></Tag>
            ),
            phone: (item) => {
                function formatPhoneNumber(phoneNumber) {
                    let cleaned = ('' + phoneNumber).replace(/\D/g, '');
                    if (cleaned[0] === '0') {
                        cleaned = cleaned.substring(1);
                    }

                    const match = cleaned.match(/^(\d{3})(\d{3})(\d{2})(\d{2})$/);
                    if (match) {
                        return '0 (' + match[1] + ') ' + match[2] + ' ' + match[3] + ' ' + match[4];
                    }
                    return phoneNumber;
                }

                return formatPhoneNumber(item);
            }
        },
        projects: {
            isArchived: (item) => (
                !item ?
                    <Tag color='green' key={item} bordered={false}><FormattedMessage id="app.open" /></Tag> :
                    <Tag color='red' key={item} bordered={false}><FormattedMessage id="app.closed" /></Tag>
            ),
            name: (item, record) => {
                if (record.clientId) {
                    return <Link href={`/app/dashboard?${URL_PARAMS.PROJECT_ID}=${record.id}`} >{item}</Link>;
                }
                return '-';
            },
            shortTitle: (item, record) => (
                record.client && record.client.shortTitle
            ),
            protocolType: (item) => (
                item === enumerations.ProtocolType.ISO ?
                    <Tag color="magenta">ISO</Tag> :
                    <Tag color="purple">GHG</Tag>
            ),
            version: (item, record) => (
                <Tooltip title={record.isCurrentVersion ? <FormattedMessage id="app.currentVersion" /> : <FormattedMessage id="app.oldVersion" />}>
                    <Text type={record.isCurrentVersion ? 'success' : 'warning'}>{item}</Text>
                </Tooltip>
            ),
            categoryType: (item) => (
                item === enumerations.CategoryTypes.Activity ?
                    <CheckCircleTwoTone twoToneColor={"#00C853"} /> :
                    <CloseCircleTwoTone twoToneColor={"#FF1744"} />
            )
        },
        notifications: {
            isRead: (item) => (
                <Tag color={item ? 'green' : 'red'} key={false} bordered={false}>{item ? <FormattedMessage id="app.read" /> : <FormattedMessage id="app.unread" />}</Tag>
            ),
        },
        files: {
            orginalFileName: (item, record) => {
                async function handleDownload(guidId, orginalFileName) {
                    await fetch(`${siteConfig.api.file.showFile}?guidId=${guidId}`, buildRequestOptions('GET'))
                        .then((response) => response.blob())
                        .then((blob) => {
                            const contentType = blob.type;

                            const fileURL = URL.createObjectURL(blob);

                            const a = document.createElement('a');
                            a.href = fileURL;
                            a.download = `${orginalFileName}.${getFileTypeExtensionFromContentType(contentType)}`;
                            a.click();

                            URL.revokeObjectURL(fileURL);
                        })
                        .catch((error) => console.error('Dosya indirme hatası:', error));
                }

                return <Button type="link" onClick={() => { handleDownload(record.guidId, item); }}>{item}</Button>
            },
            size: (item) => (
                `${item} KB`
            ),
            reference: (item, record) => {
                if (!record) {
                    return null;
                }

                let content;
                if (typeof record.client !== 'undefined' && typeof record.project !== 'undefined' && typeof record.tabKey !== 'undefined') {
                    content = <Text>{record.client.shortTitle} <Text style={{ marginLeft: '0.2rem' }}>{`->`}</Text> <Button icon={<LinkOutlined />} type="link" danger size="small" disabled href={`/app/${record.client.id}/dashboard/mainForm#${record.project.id}`}>{record.project.name}</Button> {`->`} <Button icon={<LinkOutlined />} type="link" danger size="small" disabled={typeof tabKey !== 'undefined'} href={record.plantId ?  `/app/${record.client.id}/reports/activityData?${URL_PARAMS.PROJECT_ID}=${record.project.id}&${URL_PARAMS.PLANT_ID}=${record.plantId}#${record.tabKey}` : `/app/${record.client.id}/reports/activityData?${URL_PARAMS.PROJECT_ID}=${record.project.id}#${record.tabKey}`}>{translateTabName(record.tabKey)}</Button></Text>;
                } else if (typeof record.client !== 'undefined' && typeof record.project !== 'undefined') {
                    content = <Text>{record.client.shortTitle} <Text style={{ marginLeft: '0.2rem' }}>{`->`}</Text> <Button icon={<LinkOutlined />} type="link" danger size="small" href={`/app/${record.client.id}/dashboard/mainForm#${record.project.id}`}>{record.project.name}</Button></Text>;
                } else if (typeof record.client !== 'undefined') {
                    content = <Text>{record.client.shortTitle}</Text>;
                } else {
                    content = <Text>Sistem Dosyası</Text>;
                }
                return (
                    content
                );
            }
        }
    },
    titleRenders: {
        general: {
            roleDescription: <Tooltip title="Kullanıcının işlem sırasındaki rolünü göstermektedir. Güncel rolü ile aynı olmayabilir.">Yetki</Tooltip>,
            canCustomerViewFiles: <Tooltip title="Kullanıcıların müşteri dosyalarını görüntüleyebilme ve oluşturabilme yetkisini göstermektedir.">D. İzni</Tooltip>,
            version: <Tooltip title='Projede kullanılan Emisyon Faktörleri sürümünü belirtir'><FormattedMessage id="project.column.version" /></Tooltip>
        }
    }
};

function extendedColumns(columns, pageKey, titleKey = "general", setJson, setVisibleModal) {
    const processColumn = (column) => {
        const updatedColumn = { ...column };

        if (column.type === "number") {
            updatedColumn.render = (item) => useNumberFormat(item);
        } else if (column.type === "date") {
            updatedColumn.render = (item) => item ? <DateDisplay date={item} format bordered={false} /> : <Tag />;
        } else if (column.type === "percentage") {
            updatedColumn.render = (item) => FormatPercentage(item);
        } else if (column.type === "json" && typeof setJson === 'function' && typeof setVisibleModal === 'function') {
            updatedColumn.render = (item) => <Button shape="circle" onClick={() => { setJson(parseJSON(item)); setVisibleModal(true); }} disabled={item && typeof item.disabledJsonButton === 'boolean' ? item.disabledJsonButton : !item}><ContainerOutlined /></Button>
        }

        const titleRenders = tableFunctions.titleRenders[titleKey]?.[column.key];
        if (titleRenders) {
            updatedColumn.title = titleRenders;
        }

        let renderFunction = tableFunctions.renderFunctions["general"]?.[column.key];
        if (!renderFunction) {
            renderFunction = tableFunctions.renderFunctions[pageKey]?.[column.key];
        }
        if (typeof renderFunction === 'function') {
            updatedColumn.render = renderFunction;
        }

        if (column.children) {
            updatedColumn.children = column.children.map(processColumn);
        }

        if (column.titleKey) {
            updatedColumn.title = <FormattedMessage id={column.titleKey} />;
        }

        return updatedColumn;
    };

    return columns.map(processColumn);
}

/**
 * Calculates the distance between two points using the Haversine formula in kilometers.
 * @param {Object} from - Departure location ({ latitude, longitude })
 * @param {Object} to - Arrival location ({ latitude, longitude })
 * @returns {number|null} - Distance in kilometers
 */
const calculateDistanceKm = (from, to) => {
    const toRadians = (degrees) => {
        return degrees * (Math.PI / 180);
    };

    if (
        !from?.latitude || !from?.longitude ||
        !to?.latitude || !to?.longitude
    ) {
        return null;
    }

    const R = 6371;
    const dLat = toRadians(to.latitude - from.latitude);
    const dLon = toRadians(to.longitude - from.longitude);

    const lat1 = toRadians(from.latitude);
    const lat2 = toRadians(to.latitude);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) *
        Math.cos(lat1) * Math.cos(lat2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return Math.round(distance * 10) / 10;
};

/**
 * Disables all non-leaf (parent) nodes in a tree structure.
 * Only leaf nodes remain selectable.
 *
 * @param {Array} nodes - Tree data array
 * @returns {Array} - New tree data with parent nodes disabled
 */
const disableParentNodes = (nodes) => {
    return nodes.map((node) => {
        if (node.children && node.children.length > 0) {
            return {
                ...node,
                disabled: true,
                children: disableParentNodes(node.children),
            };
        }
        return node;
    });
};

/**
 * Rounds and formats the number based on the provided parameters.
 * @param {number} number - The number to be formatted.
 * @param {number} [decimalPlaces=2] - The number of decimal places to display.
 * @param {boolean} [isPercent=false] - Specifies if the number should be displayed as a percentage.
 * @returns {string} - The formatted number.
 */
const formatNumber = (number, decimalPlaces = 2, isPercent = false) => {
    const intl = useIntl();
    const locale = intl.locale;

    const options = {
        style: isPercent ? 'percent' : 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    };

    // Sayıyı yuvarla ve formatla
    return intl.formatNumber(number, options);
};

function parseVersion(str) {
    const RX = /^(?<yy>\d{2})(?<mm>\d{2})(?<dd>\d{2})(?:v(?<rev>\d+))?$/;

    if (typeof str !== "string") throw new TypeError("version must be string");
    const m = str.trim().match(RX);
    if (!m) throw new Error(`Geçersiz sürüm: '${str}'`);

    const yy = Number(m.groups.yy);
    const mm = Number(m.groups.mm);
    const dd = Number(m.groups.dd);
    const rev = m.groups.rev ? Number(m.groups.rev) : 0;

    const year = 2000 + yy;

    const d = new Date(year, mm - 1, dd);
    const valid =
        d.getFullYear() === year && d.getMonth() === mm - 1 && d.getDate() === dd;
    if (!valid) throw new Error(`Geçersiz tarih: '${str}'`);

    return {
        date: d,
        rev,
        toString() {
            const y = String(year).slice(-2);
            const m2 = String(mm).padStart(2, "0");
            const d2 = String(dd).padStart(2, "0");
            return `${y}${m2}${d2}` + (rev > 0 ? `v${rev}` : "");
        },
    };
}

function compareVersions(a, b) {
    const va = typeof a === "string" ? parseVersion(a) : a;
    const vb = typeof b === "string" ? parseVersion(b) : b;

    if (va.date.getTime() !== vb.date.getTime()) {
        return va.date - vb.date;
    }
    return va.rev - vb.rev;
}

function isLegacy(verStr) {
    const LEGACY_THRESHOLD = parseVersion("250604");
    const v = parseVersion(verStr);
    return compareVersions(v, LEGACY_THRESHOLD) <= 0;
}

function isLegacyByDate(verStr) {
    const { date } = parseVersion(verStr);
    const thresholdDate = parseVersion("250604").date;
    return date.getTime() <= thresholdDate.getTime();
}

const getSequentialColor = (colorIndex) => {
    const colorList = [
        '#6E6E6E',
        '#5A5F75',
        '#9AA0A6',
        '#4F6672',
        '#6B806F',
        '#7A6F7B',
        '#596B6F',
        '#818A96',
        '#6F6D82',
        '#8A968A'
    ];

    return colorList[(colorIndex + 1) % colorList.length];
};

export {
    buildRequestOptions,
    checkAccessPermission,
    findMenuKeys,
    isStrInteger,
    titleCase,
    formatCamelToHyphen,
    extendedColumns,
    getSessionProperties,
    handleDateRangeChange,
    mimeToExtension,
    renderTreeSelect,
    filterTreeNode,
    getFileIcon,
    formatSize,
    getFileTypeExtensionFromContentType,
    getCalculationTypeLabel,
    getCalculationTypeColor,
    calculateDistanceKm,
    disableParentNodes,
    formatNumber,
    translateTabName,
    getSequentialColor,
    isLegacy,
    isLegacyByDate,
    tableFunctions
};